import React, { useEffect, useRef, useState } from "react";
import StyledAuthentification from "./StyledAuthentification";
import Modal from "react-modal";
import { ReactComponent as Warning } from "../../assets/icons/warning-red.svg";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Register from "./Register";
import { ReactComponent as PathfinderLogoLeftCorner } from "../../assets/images/Pathifnder Logo_White.svg";
import { ReactComponent as PathfinderMainLogo } from "../../assets/images/Pathfinder-main-logo.svg";
import healthbeaconContactDetails from "../../assets/images/Healthbeacon-contact-details.svg";
import { Auth } from "../../routes/Constants";
import APIVariables from "../../services/api-variables";
import LocalStorage from "../../shared/localStorage";
import {
  handleCatch,
  keepOnlyDigits,
  redirectToHomepageDependingOnUser,
  validateEmail,
} from "../../shared/helpers";
import { errorMessages } from "../../constants/errorMessages.constants";
import { registrationCategory } from "../../constants/register.constants";
import PasswordExpiredModal from "./PasswordExpiredModal";
import Axios from "../../services/Axios";

import ReCAPTCHA from "react-google-recaptcha";
import MFAModal from "./MFAModal";
import { clearCacheData } from "../../hooks/ClearCache/ClearCache&Cookie";

const Authentification = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [registerOpen, setRegisterOpen] = useState(
    () => searchParams.get("register") === "1"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({
    email: "",
    password: "",
    isLoggedIn: false,
  });
  const [maxAttemptsModal, setMaxAttemptsModal] = useState(false);

  const [message, setMessage] = useState({
    status: "",
    email: "",
    password: "",
    apiErrors: "",
  });
  const [errorMessage, setErrorMessage] = useState("");

  const [openModalSessionExpired, setOpenModalSessionExpired] = useState(false);
  const existSessionExpireMessage = localStorage.getItem(
    LocalStorage.EXPIRED_SESSION_MESSAGE_NHSPORTAL
  );

  const [passwordExpiredModalisOpen, setPasswordExpiredModalisOpen] =
    useState(false);
  const [resetPasswordLink, setResetPasswordLink] = useState("");

  const captchaRef = useRef(null);
  const [isCapchaNeeded, setIsCapchaNeeded] = useState(false);

  const [codeAuthentication, setCodeAuthentication] = useState({
    MAFModalIsOpen: false,
    errorMessage: "",
  });
  const [resendEmail, setResendEmail] = useState("");

  useEffect(() => {
    if (!!existSessionExpireMessage) {
      setOpenModalSessionExpired(true);
      // Clear backend session/cookies so next login doesn't hit CSRF/referer issues.
      Axios.get(APIVariables.USERS_LOGOUT).catch(() => { });
    }

    checkIsTokenNeeded();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchParams.get("register") === "1") {
      setRegisterOpen(true);
    }
  }, [searchParams]);

  const openRegister = () => setRegisterOpen(true);

  const closeRegister = () => {
    setRegisterOpen(false);
    if (searchParams.get("register")) {
      setSearchParams({}, { replace: true });
    }
  };

  const checkIsTokenNeeded = async (error) => {
    const res = await Axios.get(APIVariables.USERS_LOGIN, {}).catch((error) => {
      handleCatch(error);
      setIsLoading(false);
    });

    if (res?.status === 200) {
      setIsCapchaNeeded(() => {
        setIsLoading(false);
        setErrorMessage(error);
        return res.data.require_captcha;
      });
    }
  };

  const closeSessionExpired = () => {
    setOpenModalSessionExpired(false);
    localStorage.removeItem(LocalStorage.EXPIRED_SESSION_MESSAGE_NHSPORTAL);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginCredentials((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeCodeAuthentication = (e) => {
    const { name, value } = e.target;

    setCodeAuthentication((prevState) => ({
      ...prevState,
      ...{ [name]: keepOnlyDigits(value) },
    }));
  };
  const resendMFA = async () => {
    const res = await Axios.post(APIVariables.USER_RESEND_MFA, {
      email: loginCredentials.email,
    }).catch((error) => {
      handleCatch(error);
    });

    if (res?.status === 201 || res?.status === 200) {
      setResendEmail("Verification code resent");
      setTimeout(() => {
        setResendEmail("");
      }, 10000);
    }
  };
  const handleConfirmModal = () => {
    if (
      !codeAuthentication.mfa_code ||
      codeAuthentication.mfa_code.length !== 6
    ) {
      setCodeAuthentication((prevState) => ({
        ...prevState,
        ...{ errorMessage: "Invalid MFA code." },
      }));
    } else {
      setCodeAuthentication((prevState) => ({
        ...prevState,
        ...{
          mfa_code: codeAuthentication.mfa_code,
        },
      }));
      onSubmit({ preventDefault: () => { } });
    }
  };

  const updateCache = () => {
    try {
      Axios.post(APIVariables.RESET_CACHE, {});
    } catch (error) {
      handleCatch(error);
    }
  };

  const checkInputs = async (email, password) => {
    var error = false;
    var errors = {
      status: "",
      email: "",
      password: "",
      apiErrors: "",
    };

    if (!validateEmail(email)) {
      errors.email = errorMessages.INVALID_EMAIL;
      error = true;
    }

    if (password.length === 0) {
      errors.password = errorMessages.INVALID_PASSWORD;
      error = true;
    }

    if (error) errors.status = "error";
    setMessage(errors);

    return errors;
  };
  const releaseNotesAddIntoLocalStorage = async () => {
    const res = await Axios.get(APIVariables.RELEASE_NOTES_LIST).catch(
      (error) => {
        handleCatch(error);
      }
    );

    if (res?.status === 200) {
      localStorage.setItem(
        LocalStorage.ARE_NEW_RELEASE_NOTES,
        JSON.stringify(res.data)
      );
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    const token = captchaRef?.current?.getValue();
    captchaRef.current?.reset();

    const data = {
      email: loginCredentials.email,
      password: loginCredentials.password,
      ...(isCapchaNeeded && { token }),
      mfa_code: codeAuthentication?.mfa_code,
    };

    setIsCapchaNeeded(false);

    const errors = await checkInputs(data.email, data.password);

    if (errors.status !== "error") {
      try {
        // Clear any existing session/cookies before login to avoid CSRF/referer issues.
        await Axios.get(APIVariables.USERS_LOGOUT).catch(() => { });

        const getData = await Axios.post(APIVariables.USERS_LOGIN, {
          ...data,
        });

        if (!!getData?.data?.require_mfa) {
          setCodeAuthentication((prevState) => ({
            ...prevState,
            ...{ MAFModalIsOpen: true },
          }));
          setIsLoading(false);
        } else {
          submitLoginCall(getData);
        }
      } catch (error) {
        const errors = error?.response?.data;
        let errorToSend;
        if (
          (errors?.non_field_errors && errors?.non_field_errors[0])?.includes(
            "Invalid MFA code."
          )
        ) {
          setCodeAuthentication((prevState) => ({
            ...prevState,
            ...{ errorMessage: "Invalid MFA code." },
          }));
        } else {
          setCodeAuthentication((prevState) => ({
            ...prevState,
            ...{
              MAFModalIsOpen: false,
            },
          }));
        }

        if (typeof errors === "string") {
          errorToSend = [errorMessages.INTERNAL_SERVER_ERROR];
        } else {
          if (
            (errors?.non_field_errors && errors?.non_field_errors[0])?.includes(
              "Max login attempts reached."
            )
          ) {
            setMaxAttemptsModal(true);
          }
          if (errors?.password_expired?.includes("True")) {
            setPasswordExpiredModalisOpen(true);
            setResetPasswordLink(errors.password_reset_url[0]);
            return;
          }
          const errorsArray = [];
          Object?.keys(errors).forEach((key) => {
            errorsArray.push(errors[key]);
          });
          errorToSend = errorsArray;
        }

        checkIsTokenNeeded(errorToSend);
      }
    } else {
      setIsLoading(false);
    }
  };

  const submitLoginCall = async (getData) => {
    try {
      if (getData?.data?.display_release_notes === true) {
        releaseNotesAddIntoLocalStorage();
      }

      const res = await Axios.get(APIVariables.USERS_PROFILE).catch((error) => {
        handleCatch(error);
        setIsLoading(false);
        return null;
      });

      if (!res?.data) return;

      const user = {
        isLoggedIn: true,
        ...res.data,
        ...(res.data.user_type === registrationCategory.PRESCRIBER && {
          ...res.data?.prescriber,
        }),
        ...(res.data.user_type === registrationCategory.PHARMACY && {
          ...res.data?.connected_nurse?.prescriber,
        }),
        ...(res.data.user_type === registrationCategory.CONNECTED_NURSE && {
          ...res.data?.pharmacy,
        }),
      };

      if (getData?.data?.force_reset_cache === true && user.isLoggedIn) {
        clearCacheData();
        updateCache();
      }

      localStorage.setItem(LocalStorage.USER_NHSPORTAL, JSON.stringify(user));
      const nextRoute = localStorage.getItem(LocalStorage.NEXT_PAGE_NHSPORTAL);
      if (nextRoute) {
        window.location.href = nextRoute;
        localStorage.removeItem(LocalStorage.NEXT_PAGE_NHSPORTAL);
      } else {
        if (getData?.data) {
          let route = redirectToHomepageDependingOnUser(res.data.user_type);
          window.location.href = route;
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const continueModalQuit = () => {
    setPasswordExpiredModalisOpen(false);
    setResetPasswordLink("");

    window.location.href = resetPasswordLink;
  };

  return (
    <StyledAuthentification data-testid="auth-login-page">
      <header className="login-z flex shrink-0 items-center px-6 py-6 sm:px-10">
        <PathfinderLogoLeftCorner
          className="h-[34px] w-auto max-w-[min(100%,186px)] sm:h-[42px]"
          aria-label="PATHFINDER Connect"
          role="img"
        />
      </header>

      <main
        className="login-z flex flex-1 flex-col items-center justify-center px-4 pb-10 pt-2"
        data-testid="auth-login-main"
      >
        <div
          className="w-full max-w-[520px] rounded-[2.75rem] border border-white/30 bg-white/18 px-12 py-12 shadow-[0_8px_40px_rgba(0,0,0,0.18)] backdrop-blur-[22px]"
          style={{ WebkitBackdropFilter: "blur(22px)" }}
          data-testid="auth-login-card"
        >
          <div className="mb-10 flex flex-col items-center text-center">
            <PathfinderMainLogo
              className="h-auto w-full max-w-[min(100%,280px)] text-white sm:max-w-[305px]"
              aria-label="PATHFINDER — Supporting Safe Medication Care"
              role="img"
            />
          </div>

          <form
            className="flex flex-col gap-5"
            onSubmit={onSubmit}
            noValidate
            data-testid="auth-login-form"
          >
            <div className="flex flex-col gap-2">
              <label
                htmlFor="login-email"
                className="ml-2 text-sm font-semibold text-white"
                data-testid="auth-login-email-label"
              >
                Email Address
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                placeholder="Email"
                onChange={handleChange}
                name="email"
                value={loginCredentials?.email || ""}
                data-testid="auth-login-email-input"
                className={
                  "w-full rounded-lg border-0 bg-white px-4 py-2 text-[15px] text-[#374151] shadow-inner outline-none ring-2 ring-transparent placeholder:text-gray-400 focus:ring-2 focus:ring-white/70 " +
                  (message.email?.length > 0 ? "ring-amber-300" : "")
                }
              />
              {message.email ? (
                <span
                  className="text-sm text-amber-100"
                  data-testid="auth-login-email-error"
                >
                  {message.email}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="login-password"
                className="ml-2 text-sm font-semibold text-white"
                data-testid="auth-login-password-label"
              >
                Password
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                onChange={handleChange}
                name="password"
                value={loginCredentials?.password || ""}
                data-testid="auth-login-password-input"
                className={
                  "w-full rounded-lg border-0 bg-white px-4 py-2 text-[15px] text-[#374151] shadow-inner outline-none ring-2 ring-transparent placeholder:text-gray-400 focus:ring-2 focus:ring-white/70 " +
                  (message.password?.length > 0 ? "ring-amber-300" : "")
                }
              />

              <div className="mt-1 flex flex-wrap items-center justify-between gap-2 pt-0.5">
                <Link
                  to={Auth.RESET_PASSWORD}
                  className="ml-2 text-sm text-white/95 decoration-white/50 hover:text-white"
                  data-testid="auth-login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>

              {isCapchaNeeded ? (
                <div
                  className="flex justify-center pt-2 [&>div]:scale-[0.92]"
                  data-testid="auth-login-recaptcha"
                >
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_SITE_KEY}
                    ref={captchaRef}
                  />
                </div>
              ) : null}

              {message.password ? (
                <span
                  className="text-sm text-amber-100"
                  data-testid="auth-login-password-error"
                >
                  {message.password}
                </span>
              ) : null}
              {message.apiErrors ? (
                <span
                  className="text-sm text-amber-100"
                  data-testid="auth-login-api-errors"
                >
                  {message.apiErrors}
                </span>
              ) : null}
              <div data-testid="auth-login-error-messages">
                {(errorMessage || []).map((item, index) => (
                  <span className="text-sm text-amber-100" key={index}>
                    {item[0] !== "Invalid MFA code." && item}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4 pl-2 pr-6 flex w-full gap-5">
              <button
                type="submit"
                disabled={isLoading}
                data-testid="auth-login-submit-button"
                className="flex min-h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-white px-3 text-[15px] font-semibold text-[#0f172a] shadow-md transition hover:bg-white/95 disabled:opacity-60"
              >
                Login
              </button>
              <button
                type="button"
                onClick={openRegister}
                data-testid="auth-login-register-button"
                className="inline-flex min-h-10 min-w-0 flex-2 items-center justify-center rounded-full border-2 border-white bg-transparent px-3 text-center text-[15px] font-semibold leading-tight text-white transition hover:bg-white/10"
              >
                Register for an account
              </button>
            </div>
          </form>
        </div>
      </main>

      <footer
        className="login-z mt-auto flex justify-center px-6 pb-10 pt-4 sm:px-10"
        data-testid="auth-login-footer"
      >
        <img
          src={healthbeaconContactDetails}
          alt="HealthBeacon — Smart Tools for Managing Medication. For support, contact Pharmacare Group."
          className="h-auto w-full max-w-md select-none sm:max-w-lg"
          loading="lazy"
          decoding="async"
        />
      </footer>
      <Modal
        isOpen={maxAttemptsModal}
        onRequestClose={() => setMaxAttemptsModal(false)}
        style={customStyles}
        contentLabel="Error Modal"
        overlayClassName="overlay-modal"
        shouldCloseOnOverlayClick={false} // add this to prevent outside click to prevent modal close
      >
        <div
          className="wrapper-modal"
          data-testid="auth-max-attempts-modal"
        >
          <div className="image">
            <Warning />
          </div>
          <div
            className="main-text-error"
            data-testid="auth-max-attempts-heading"
          >
            Account Locked
          </div>
          <div
            className="second-text"
            data-testid="auth-max-attempts-description"
          >
            You have had 3 incorrect password attempts. Your password is now
            locked. Please return to the login screen and reset your password.
          </div>
        </div>
        <div className="wrapper-buttons">
          <button
            className="red-button"
            onClick={() => navigate(Auth.RESET_PASSWORD)}
            data-testid="auth-max-attempts-confirm-button"
          >
            Confirm
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={openModalSessionExpired}
        style={customStyles}
        contentLabel="Session Expired Modal"
        overlayClassName="overlay-modal"
        shouldCloseOnOverlayClick={false} // add this to prevent outside click to prevent modal close
      >
        <div
          className="wrapper-modal"
          data-testid="auth-session-expired-modal"
        >
          <div className="image">
            <Warning />
          </div>
          <div
            className="main-text-error"
            data-testid="auth-session-expired-heading"
          >
            Session Expired
          </div>
          <div
            className="second-text"
            data-testid="auth-session-expired-description"
          >
            {existSessionExpireMessage}
          </div>
        </div>
        <div className="wrapper-buttons">
          <button
            className="red-button"
            onClick={closeSessionExpired}
            data-testid="auth-session-expired-login-button"
          >
            Login
          </button>
        </div>
      </Modal>

      <PasswordExpiredModal
        passwordExpiredModalisOpen={passwordExpiredModalisOpen}
        continueModalQuit={continueModalQuit}
      />

      <MFAModal
        resendEmail={resendEmail}
        resendMFA={resendMFA}
        MAFModalIsOpen={codeAuthentication.MAFModalIsOpen}
        confirmMFAModal={handleConfirmModal}
        handleChangeCodeAuthentication={handleChangeCodeAuthentication}
        errorMessage={codeAuthentication.errorMessage}
        mfa_code={codeAuthentication.mfa_code}
      />

      {registerOpen ? (
        <Register embedded onClose={closeRegister} />
      ) : null}
    </StyledAuthentification>
  );
};
export default Authentification;

let customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    minWidth: 515,
    width: "34%",
    boxShadow: "0px 4px 20px rgb(0 0 0 / 20%)",
    borderRadius: 30,
  },
};
